import { EventEmitter, Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CONFIG_CONTEXT } from '../enum/config-context.enum';
import { KingfisherService } from './kingfisher/kingfisher.service';
import { CreateCustomer } from './kingfisher/create-customer';
import { Customer } from './kingfisher/customer';
import { Token } from './kingfisher/token';
import { CustomerSession } from './customer-session';
import { CurrentService } from './current.service';
import { environment } from 'src/environments/environment';
import { logError } from './helpers/sentry.helper';

export const CUSTOMER_EVENT_NEW_PROJECT_REQUESTED = 'NEW_PROJECT_REQUESTED';

/** Marge avant expiration (ms) sous laquelle on renouvelle le jeton Kingfisher de façon anticipée. */
const KINGFISHER_TOKEN_REFRESH_MARGIN_MS = 30000;

@Injectable({
  providedIn: 'root',
})
export class CustomerService {
  public context: string = CONFIG_CONTEXT.KITCHEN;
  public token: string;
  public tokenInfos: any;
  public userDatas: Customer;

  /**
   * Jeton d'authentification **client Kingfisher** complet (`access_token`, `refresh_token`, `expires_in`…).
   * Conservé à part de `token` car ce dernier est écrasé par le flux HBM/ByMe
   * (`HbmApiService.acquireTokenWS`). Utiliser {@link getValidKingfisherToken} pour obtenir un
   * `access_token` valide (rafraîchi si nécessaire) pour les appels exigeant l'identité client Kingfisher.
   */
  public kingfisherToken: Token;

  /** Instant (epoch ms) d'expiration de {@link kingfisherToken}, calculé depuis `expires_in`. */
  private kingfisherTokenExpiresAt = 0;

  /** Rafraîchissement en cours (mutualise les appels concurrents). */
  private kingfisherRefreshPromise?: Promise<void>;

  public legacyToken: string;
  public legacyCustomerId: string;

  public events: EventEmitter<any>;

  constructor(
    private readonly http: HttpClient,
    private readonly kfService: KingfisherService,
    private readonly injector: Injector
  ) {
    this.events = new EventEmitter();
  }

  get isLogged(): boolean {
    return this.token != null && this.userDatas != null;
  }

  get fullName(): string {
    return !this.isLogged
      ? ''
      : `${this.userDatas?.firstname?.charAt(0)?.toUpperCase()}${this.userDatas?.firstname?.slice(1)} ${this.userDatas?.lastname?.toUpperCase()}`;
  }

  logout(): void {
    this.token = null;
    this.kingfisherToken = null;
    this.kingfisherTokenExpiresAt = 0;
    this.tokenInfos = null;
    this.userDatas = null;
  }

  /** Retourne un instantané de la session du client identifié. */
  captureSession(): CustomerSession {
    return {
      token: this.token,
      tokenInfos: this.tokenInfos,
      userDatas: this.userDatas,
      kingfisherToken: this.kingfisherToken,
      kingfisherTokenExpiresAt: this.kingfisherTokenExpiresAt,
    };
  }

  /** Rétablit la session du client à partir d'un instantané. */
  restoreSession(session: CustomerSession): void {
    this.token = session.token;
    this.tokenInfos = session.tokenInfos;
    this.userDatas = session.userDatas;
    this.kingfisherToken = session.kingfisherToken;
    this.kingfisherTokenExpiresAt = session.kingfisherTokenExpiresAt;
  }

  /**
   * Renvoie un `access_token` client Kingfisher **valide** : rafraîchit le jeton (via `refresh_token`)
   * s'il est expiré ou proche de l'expiration. Renvoie `undefined` si aucun jeton client n'est disponible.
   */
  async getValidKingfisherToken(): Promise<string> {
    if (!this.kingfisherToken?.access_token) {
      return undefined;
    }

    if (this.isKingfisherTokenExpiring() && this.kingfisherToken.refresh_token) {
      await this.refreshKingfisherToken();
    }

    return this.kingfisherToken.access_token;
  }

  /** Enregistre le jeton client Kingfisher et calcule son instant d'expiration depuis `expires_in`. */
  private setKingfisherToken(token: Token): void {
    this.kingfisherToken = token;
    this.kingfisherTokenExpiresAt = token?.expires_in ? Date.now() + token.expires_in * 1000 : 0;
  }

  /** Le jeton est-il expiré ou dans la marge de renouvellement anticipé ? */
  private isKingfisherTokenExpiring(): boolean {
    return Date.now() >= this.kingfisherTokenExpiresAt - KINGFISHER_TOKEN_REFRESH_MARGIN_MS;
  }

  /** Rafraîchit le jeton via son `refresh_token`, en mutualisant les appels concurrents. */
  private refreshKingfisherToken(): Promise<void> {
    this.kingfisherRefreshPromise ??= (async () => {
      try {
        this.setKingfisherToken(await this.kfService.refresh(this.kingfisherToken.refresh_token));
      } catch (err) {
        // On conserve le jeton actuel : l'appel en aval échouera proprement s'il est réellement expiré.
        logError(err);
      } finally {
        this.kingfisherRefreshPromise = undefined;
      }
    })();

    return this.kingfisherRefreshPromise;
  }

  lookForLegacyCustomerId(sapId: string): Promise<string> {
    return new Promise((resolve, reject) => {
      console.time('lookForLegacyCustomerId');

      let _localDbFile: string = '/assets/utils/legacyID_to_sapID/s2l_' + sapId.slice(-2) + '.json';

      this.http.get(_localDbFile).subscribe(
        (localBase: any) => {
          let _unpreffixedSapID = sapId.replace('SQ_', '');
          let _found = localBase.find((row: any) => {
            return row[0] == _unpreffixedSapID;
          });

          if (_found) {
            console.timeEnd('lookForLegacyCustomerId');
            this.legacyCustomerId = _found[1];
            resolve(_found[1]);
          } else {
            resolve(null);
          }
        },
        (error: any) => {
          resolve(null);
        }
      );
    });
  }

  async login(username: string, password: string): Promise<void> {
    try {
      const token = await this.kfService.login(username, password);
      this.token = token.access_token;
      this.setKingfisherToken(token);
      this.userDatas = await this.kfService.getCustomerInfo(this.token);
    } catch (err) {
      logError(err);
      throw err;
    }
  }

  async forgottenPassword(email: string): Promise<void> {
    await this.kfService.forgottenPassword(email);
  }

  async register(formData: CreateCustomer): Promise<void> {
    try {
      if (environment.vendor) {
        // Insertion du CurrentService en lazy loading à cause d'une référence cyclique entre CurrentService et CustomerService
        const current = this.injector.get(CurrentService);
        const token = await current.vendor.accessToken();
        this.userDatas = await this.kfService.registerByColleague(formData, token);
      } else {
        const result = await this.kfService.register(formData);
        this.token = result.token.access_token;
        this.setKingfisherToken(result.token);
        this.userDatas = result.customer;
      }
    } catch (err) {
      logError(err);
      throw err;
    }
  }

  async loadUserByKfId(kfId: string): Promise<void> {
    try {
      if (environment.vendor) {
        const current = this.injector.get(CurrentService);
        const token = await current.vendor.accessToken();
        this.userDatas = await this.kfService.getCustomerInfoByColleague(token, kfId);
      }
    } catch (err) {
      logError(err);
      throw err;
    }
  }
}
